// src/components/digital_twin/DigitalTwinOverlay.js
// FlowSense MOW+ — Asset Digital Twin drawer (v6)
// --- UPDATE (twin overlay v6): rebuilt as a thin composition over twin_engine/.
//   • Knows what it is showing (assetProfile: drainage well, water well, catch basin, drain,
//     pipeline, pump, tank, road, …) — icon, wording, sources and tabs follow the kind.
//   • Real backend data for every asset, with or without a sensor (v5 only built its snapshot when a
//     sensor was linked, so most assets showed an empty "PAUSED" panel): ML health + history, KPIs,
//     maintenance, alerts, TessaDEM elevation / terrain, well intelligence (wells), sensor history.
//   • FlowSense AI explainer: rating, evidence, actions, data gaps + ask FlowSense AI with the full
//     asset context. Every value cites its source; missing data is shown as missing.
//   • Readable UI (≥ 11 px, Chakra tokens, light / dark); the nested TimeSlider is gone — the page
//     timeline drives replay and the drawer shows a REPLAY badge.
//   • Same default export and props as v5; new optional props: onClose, risk, onFocusAsset, assetHealth.
// --- END UPDATE
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Box, Flex, Icon, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import { FaBrain, FaInfoCircle, FaSatelliteDish, FaHeartbeat, FaBell, FaTools, FaProjectDiagram, FaCloudShowersHeavy, FaCube } from 'react-icons/fa';
import useAssetTwin from './twin_engine/useAssetTwin';
import { ASSET_KINDS } from './twin_engine/assetProfile';
import { normAlert, normHealth } from './twin_engine/twinData';
import { buildBrief, sensorRows } from './twin_engine/twinExplainer';
import { Empty, useTwinTokens } from './twin_engine/ui/ui';
import TwinHeader from './twin_engine/ui/TwinHeader';
import DataSourcesBar from './twin_engine/ui/DataSourcesBar';
import AiExplainerTab from './twin_engine/ui/AiExplainerTab';
import OverviewTab from './twin_engine/ui/OverviewTab';
import TelemetryTab from './twin_engine/ui/TelemetryTab';
import HealthTab from './twin_engine/ui/HealthTab';
import AlarmsTab from './twin_engine/ui/AlarmsTab';
import MaintenanceTab from './twin_engine/ui/MaintenanceTab';
import NetworkTab from './twin_engine/ui/NetworkTab';
import SimulationTab from './twin_engine/ui/SimulationTab';

const EMPTY = Object.freeze([]);
const EMPTY_OBJ = Object.freeze({});

function DigitalTwinOverlay({
  selectedAsset, sensors = EMPTY, readings = EMPTY_OBJ, onCriticalAlarm, className, isLive = true, historicalTimestamp = null,
  simulationRecord = null, simulationMeta = null, simulationDownstreamCount = 0,
  onClose, risk = null, onFocusAsset, assetHealth = null,
}) {
  const t = useTwinTokens();
  const { profile: baseProfile, sources, health: fetchedHealth, historical, loading, refresh, lastPush } = useAssetTwin({
    asset: selectedAsset, sensors, isLive, historicalTimestamp,
  });

  // Well intelligence knows whether a "Well" is a drainage or a water well — trust it over the type text.
  const profile = useMemo(() => {
    const kind = sources.wellIntel?.data?.well_kind;
    if (baseProfile.wellIntel && kind === 'drainage' && baseProfile.key !== 'drainage_well') return { ...baseProfile, key: 'drainage_well', ...ASSET_KINDS.drainage_well, typeLabel: baseProfile.typeLabel };
    if (baseProfile.wellIntel && kind && kind !== 'drainage' && baseProfile.key === 'drainage_well') return { ...baseProfile, key: 'water_well', ...ASSET_KINDS.water_well, typeLabel: baseProfile.typeLabel };
    return baseProfile;
  }, [baseProfile, sources.wellIntel]);

  // Replay: the historical snapshot replaces live health / alerts. Live: ML endpoint → page bulk cache.
  const replay = !isLive && historical?.status === 'ok' ? historical.data : null;
  const health = useMemo(() => {
    if (replay) return normHealth({ health_score: replay.health_score, health_status: replay.health_status });
    return fetchedHealth || normHealth(assetHealth);
  }, [replay, fetchedHealth, assetHealth]);
  const effSources = useMemo(() => {
    if (!replay) return sources;
    return { ...sources, alerts: { status: (replay.alarms || []).length ? 'ok' : 'empty', data: (replay.alarms || []).map(normAlert), at: Date.now() }, historical };
  }, [sources, replay, historical]);

  const sensorList = useMemo(() => sensorRows(sensors, readings, sources.sensorHistory?.data), [sensors, readings, sources.sensorHistory]);

  const ctx = useMemo(() => ({
    asset: selectedAsset, profile, sources: effSources, health, sensors: sensorList, simulationRecord, simulationMeta,
    downstreamCount: simulationDownstreamCount, risk, historical: replay ? { at: historicalTimestamp } : null, isLive, historicalTimestamp,
  }), [selectedAsset, profile, effSources, health, sensorList, simulationRecord, simulationMeta, simulationDownstreamCount, risk, replay, isLive, historicalTimestamp]);
  const brief = useMemo(() => (selectedAsset ? buildBrief(ctx) : null), [ctx, selectedAsset]);

  // Critical alarms → page toast, once per alert id.
  const seen = useRef(new Set());
  useEffect(() => { seen.current = new Set(); }, [selectedAsset?.id]);
  useEffect(() => {
    if (!isLive || typeof onCriticalAlarm !== 'function') return;
    const fresh = (sources.alerts?.data || []).filter((a) => a.severity === 'critical' && !seen.current.has(a.id));
    fresh.forEach((a) => seen.current.add(a.id));
    if (fresh.length) onCriticalAlarm(fresh);
  }, [sources.alerts, isLive, onCriticalAlarm]);

  const [tab, setTab] = useState(0);
  useEffect(() => { setTab(0); }, [selectedAsset?.id]);
  const alerts = effSources.alerts?.data || EMPTY;

  const tabs = useMemo(() => [
    { key: 'ai', label: 'AI Explainer', icon: FaBrain },
    { key: 'overview', label: 'Overview', icon: FaInfoCircle },
    { key: 'telemetry', label: 'Telemetry', icon: FaSatelliteDish, count: sensorList.length || null },
    { key: 'health', label: 'Health', icon: FaHeartbeat },
    { key: 'alarms', label: 'Alarms', icon: FaBell, count: alerts.length || null, hot: alerts.some((a) => a.severity === 'critical') },
    { key: 'maintenance', label: 'Maintenance', icon: FaTools },
    { key: 'network', label: profile.wellIntel ? 'Drainage' : 'Network', icon: FaProjectDiagram },
    { key: 'simulation', label: 'Simulation', icon: FaCloudShowersHeavy, count: simulationRecord ? '●' : null },
  ], [sensorList.length, alerts, profile.wellIntel, simulationRecord]);

  const refetchAlerts = useCallback(() => refresh(), [refresh]);

  if (!selectedAsset) {
    return (
      <Flex className={className} h="100%" bg={t.bg} align="center" justify="center">
        <Empty icon={FaCube} title="No asset selected">Select an asset on the twin to open its digital twin.</Empty>
      </Flex>
    );
  }

  return (
    <Flex className={className} direction="column" h="100%" bg={t.bg} color={t.text} role="dialog" aria-label={`Digital twin: ${selectedAsset.name || selectedAsset.id}`}>
      <TwinHeader asset={selectedAsset} profile={profile} brief={brief} health={health} sources={effSources} sensorCount={sensorList.length}
        isLive={isLive} historicalTimestamp={historicalTimestamp} loading={loading} lastPush={lastPush} onRefresh={refresh} onClose={onClose} />
      {!sensorList.length && (
        <Box px={5} py={2} bg="blue.50" _dark={{ bg: 'whiteAlpha.50' }} borderBottomWidth="1px" borderColor={t.border}>
          <Text fontSize="12px" color={t.muted}>No sensor linked — showing backend records only (register, ML health, inspections, terrain, work orders).</Text>
        </Box>
      )}
      <Tabs index={tab} onChange={setTab} isLazy lazyBehavior="keepMounted" colorScheme="cyan" display="flex" flexDirection="column" flex={1} minH={0}>
        <TabList px={3} bg={t.card} borderBottomWidth="1px" borderColor={t.border} overflowX="auto" overflowY="hidden" flexShrink={0}
          sx={{ scrollbarWidth: 'thin', '&::-webkit-scrollbar': { height: '4px' } }}>
          {tabs.map((x) => (
            <Tab key={x.key} fontSize="13px" fontWeight="700" px={3} py={3} whiteSpace="nowrap" gap={1.5}>
              <Icon as={x.icon} boxSize={3} />{x.label}
              {x.count != null && <Badge ml={1} colorScheme={x.hot ? 'red' : 'gray'} fontSize="9px" borderRadius="full">{x.count}</Badge>}
            </Tab>
          ))}
        </TabList>
        <TabPanels flex={1} minH={0} overflowY="auto">
          <TabPanel p={5}><AiExplainerTab brief={brief} ctx={ctx} /></TabPanel>
          <TabPanel p={5}><OverviewTab asset={selectedAsset} profile={profile} sources={effSources} /></TabPanel>
          <TabPanel p={5}><TelemetryTab sensors={sensorList} profile={profile} historical={replay ? historical : null} /></TabPanel>
          <TabPanel p={5}><HealthTab health={health} sources={effSources} profile={profile} /></TabPanel>
          <TabPanel p={5}><AlarmsTab alerts={alerts} source={effSources.alerts} isLive={isLive} onChanged={refetchAlerts} /></TabPanel>
          <TabPanel p={5}><MaintenanceTab sources={effSources} /></TabPanel>
          <TabPanel p={5}><NetworkTab profile={profile} sources={effSources} risk={risk} downstreamCount={simulationDownstreamCount} simulationMeta={simulationMeta} onFocusAsset={onFocusAsset} /></TabPanel>
          <TabPanel p={5}><SimulationTab record={simulationRecord} meta={simulationMeta} downstreamCount={simulationDownstreamCount} /></TabPanel>
        </TabPanels>
      </Tabs>
      <DataSourcesBar sources={effSources} loading={loading} />
    </Flex>
  );
}

DigitalTwinOverlay.propTypes = {
  selectedAsset: PropTypes.object,
  sensors: PropTypes.array,
  readings: PropTypes.object,
  healthHistory: PropTypes.array, // kept for v5 compatibility — history is fetched from the backend
  onCriticalAlarm: PropTypes.func,
  className: PropTypes.string,
  isLive: PropTypes.bool,
  historicalTimestamp: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.number, PropTypes.string]),
  simulationRecord: PropTypes.object,
  simulationMeta: PropTypes.object,
  simulationDownstreamCount: PropTypes.number,
  onClose: PropTypes.func,
  risk: PropTypes.object,
  onFocusAsset: PropTypes.func,
  assetHealth: PropTypes.object,
};

export default memo(DigitalTwinOverlay);
